
import { useState } from "react";

interface TimelineIconProps {
  iconName: string;
  size?: number;
  className?: string;
}

const TimelineIcon = ({ iconName, size = 48, className = "" }: TimelineIconProps) => {
  const [imageError, setImageError] = useState(false);
  
  // Detect icon type and get appropriate code
  const iconType = detectIconType(iconName);
  const openMojiCode = getOpenMojiCode(iconName, iconType);
  const openMojiUrl = `https://openmoji.org/data/color/svg/${openMojiCode}.svg`;
  
  // If it's a direct emoji or image load failed, render as text
  if (imageError || iconType === 'emoji') {
    return (
      <div 
        className={`flex items-center justify-center ${className}`}
        style={{ fontSize: `${size * 0.8}px`, width: size, height: size }}
      >
        {iconName}
      </div>
    );
  }
  
  return (
    <img
      src={openMojiUrl}
      alt={iconName}
      width={size}
      height={size}
      className={`object-contain ${className}`}
      onError={() => setImageError(true)}
      loading="lazy"
    />
  );
};

// Detect icon type based on format
function detectIconType(iconName: string): 'unicode' | 'complex' | 'emoji' | 'named' {
  // Direct Unicode (4-6 hex chars): 1F930
  if (/^[0-9A-F]{4,6}$/i.test(iconName)) return 'unicode';
  
  // Complex OpenMoji code: 1FAD9-200D-1F7E6
  if (/^[0-9A-F]{4,6}(-[0-9A-F]{4,6})+$/i.test(iconName)) return 'complex';
  
  // Direct emoji (1-2 chars)
  if (iconName.length <= 2) return 'emoji';
  
  // Named icon
  return 'named';
}

// Get OpenMoji code based on icon type
function getOpenMojiCode(iconName: string, iconType: 'unicode' | 'complex' | 'emoji' | 'named'): string {
  // For direct Unicode and complex codes, use as-is
  if (iconType === 'unicode' || iconType === 'complex') {
    return iconName.toUpperCase();
  }
  
  // For named icons, use mapping
  if (iconType === 'named') {
    const iconMap: Record<string, string> = {
      // New OpenMoji icons according to user specifications
      'sparkles': '2728',                    // ✨ Three sparkles/stars for Embryonale Phase
      'seedling-pot': '1FAB4',              // 🪴 Plant/seedling in pot for Kindheit
      'star-radiating': '1F31F',            // 🌟 Star with radiating lines for Jugend
      'signpost': '1F6A5',                  // 🚥 Signpost/crossroads for Frühes Erwachsenenalter
      'deciduous-tree': '1F333',           // 🌳 Tree for Reifephase
      'balance-scale': '2696',              // ⚖️ Balance/scales for Lebensmitte
      'butterfly': '1F98B',                 // 🦋 Butterfly for Reifejahre
      'sunrise': '1F305',                   // 🌅 Sunrise/sunset for Älteres Erwachsenenalter
      
      // Legacy mappings for backward compatibility
      'seedling': '1F331',
      'baby': '1F476',
      'child': '1F9D2',
      'school': '1F3EB',
      'books': '1F4DA',
      'star': '2B50',
      'adult': '1F9D1',
      'elderly-person': '1F9D3'
    };
    
    return iconMap[iconName] || '2753'; // Question mark as fallback
  }
  
  // For emoji type, return fallback (won't be used since emoji renders as text)
  return '2753';
}

export default TimelineIcon;
